import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api } from "../lib/api";

export default function Reviews() {
  const [data, setData] = useState({ reviews: [], details_serviced: 0 });
  const [gallery, setGallery] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, text: "", location: "", images: [] });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/reviews").then((r) => setData(r.data));
  useEffect(() => { load(); api.get("/gallery").then((r) => setGallery(r.data.gallery)); }, []);

  const onFiles = (e) => {
    const files = Array.from(e.target.files).slice(0, 3);
    Promise.all(files.map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); })))
      .then((imgs) => setForm((f) => ({ ...f, images: [...f.images, ...imgs].slice(0, 3) })));
    e.target.value = "";
  };
  const removeImg = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) { toast.error("Please add your name and review."); return; }
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/reviews", form);
      toast.success(res.message);
      setForm({ name: "", rating: 5, text: "", location: "", images: [] });
    } catch { toast.error("Could not submit review."); }
    setSubmitting(false);
  };

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <p className="text-xs tracking-[0.3em] uppercase text-pink-500 font-bold mb-4">Reviews</p>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter">What clients say</h1>
          <div className="text-right"><p className="text-4xl font-extrabold text-pink-500">{data.details_serviced}+</p><p className="text-sm text-zinc-400">details serviced</p></div>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {data.reviews.map((r, i) => {
            return (
            <motion.div key={r.id} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.05}} className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid={`review-${i}`}>
              <div className="flex gap-1 mb-3">{[...Array(r.rating)].map((_,j)=><Star key={j} size={16} className="fill-pink-500 text-pink-500" />)}</div>
              <p className="text-zinc-300 leading-relaxed">"{r.text}"</p>
              {r.images && r.images.length > 0 && (
                <div className="mt-3 flex gap-2">
                  {r.images.map((img, idx) => (
                    <img key={idx} src={img} alt={`review-${r.id}-img-${idx}`} className="w-24 h-16 object-cover rounded-md border border-white/10" />
                  ))}
                </div>
              )}
              <p className="mt-4 text-sm font-semibold">{r.name} {r.location && <span className="text-zinc-500 font-normal">· {r.location}</span>}</p>
            </motion.div>
            );
          })}
        </div>

        <div className="mt-16 rounded-2xl border border-white/10 bg-[#121212] p-8 max-w-2xl">
          <h2 className="text-2xl font-bold">Leave us a review</h2>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Your name" className="bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="review-name" />
              <input value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})} placeholder="Location (e.g. Kailua)" className="bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="review-location" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400">Rating:</span>
              {[1,2,3,4,5].map((n)=>(
                <button type="button" key={n} onClick={()=>setForm({...form,rating:n})} data-testid={`review-star-${n}`}><Star size={22} className={n<=form.rating?"fill-pink-500 text-pink-500":"text-zinc-600"} /></button>
              ))}
            </div>
            <textarea value={form.text} onChange={(e)=>setForm({...form,text:e.target.value})} placeholder="Tell us about your experience..." rows={4} className="w-full bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="review-text" />
            <div>
              <label className="inline-flex items-center gap-2 cursor-pointer bg-[#050505] border border-white/20 hover:border-pink-500 rounded-lg px-4 py-3 text-sm text-zinc-300 transition-colors" data-testid="review-upload-label">
                <ImagePlus size={18} className="text-pink-500" /> Upload photos (up to 3)
                <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="review-images" />
              </label>
              {form.images.length > 0 && (
                <div className="mt-3 flex gap-2">
                  {form.images.map((img, i) => (
                    <div key={img} className="relative">
                      <img src={img} alt="preview" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
                      <button type="button" onClick={() => removeImg(i)} className="absolute -top-2 -right-2 bg-red-500 rounded-full p-0.5" data-testid={`review-img-remove-${i}`}><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button disabled={submitting} className="bg-pink-600 hover:bg-pink-500 text-white font-semibold rounded-full px-8 py-3 transition-colors active:scale-95 disabled:opacity-50" data-testid="review-submit">{submitting?"Submitting...":"Submit Review"}</button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
